/**
 * SeeMe Lightbox
 * Full-screen image viewer with sliding photos and a thumbnail strip.
 *
 * Usage:
 *   Add [data-lightbox-group] to a container (e.g., .photo-grid).
 *   Each .photo-grid__item with an <img> becomes clickable.
 *   Optional: [data-lightbox-src="full.jpg"] on items for hi-res URL.
 *
 * API:
 *   SeeMe.lightbox.open(items, startIndex, title)
 *   SeeMe.lightbox.close()
 *
 * Behavior:
 *   - Photos slide; the current photo and its neighbors are preloaded, and the
 *     blurred thumbnail stands in until the full image arrives.
 *   - Swipe left/right to change photos, swipe down to close.  Swipes are
 *     ignored while the page is pinch-zoomed or two fingers are down, so the
 *     browser's own zoom and pan work.
 *   - Opening pushes a history entry, so the back button closes the viewer
 *     instead of leaving the page.
 *   - The header links to the current photo's URL in a new tab.
 */

var SeeMe = window.SeeMe || {};

SeeMe.lightbox = (function () {
    var root = null;
    var trackEl = null;
    var counterEl = null;
    var titleEl = null;
    var originalEl = null;
    var stripEl = null;
    var stripTrack = null;
    var prevBtn = null;
    var nextBtn = null;

    var items = [];
    var index = 0;
    var savedOverflow = '';
    var savedOverscroll = '';

    // True while the current history entry is the one open() pushed.  Seeded
    // from history.state so a reload on that entry reuses it instead of
    // stacking a second one.
    var onOwnEntry = !!(history.state && history.state.seemeLightbox);

    // True between our own history.back() and the popstate it causes.
    var backPending = false;

    function create() {
        root = document.createElement('div');
        root.className = 'lightbox';
        root.innerHTML =
            '<div class="lightbox__header">' +
                '<span class="lightbox__counter"></span>' +
                '<span class="lightbox__title"></span>' +
                '<a class="lightbox__original" target="_blank" rel="noopener" aria-label="Open original in new tab" title="Open original"><i class="fa-solid fa-arrow-up-right-from-square"></i></a>' +
                '<button class="lightbox__close" type="button" aria-label="Close"><i class="fa-solid fa-xmark"></i></button>' +
            '</div>' +
            '<button class="lightbox__nav lightbox__nav--prev" type="button" aria-label="Previous"><i class="fa-solid fa-chevron-left"></i></button>' +
            '<button class="lightbox__nav lightbox__nav--next" type="button" aria-label="Next"><i class="fa-solid fa-chevron-right"></i></button>' +
            '<div class="lightbox__stage"><div class="lightbox__track"></div></div>' +
            '<div class="lightbox__strip"><div class="lightbox__strip-track"></div></div>';

        trackEl = root.querySelector('.lightbox__track');
        counterEl = root.querySelector('.lightbox__counter');
        titleEl = root.querySelector('.lightbox__title');
        originalEl = root.querySelector('.lightbox__original');
        stripEl = root.querySelector('.lightbox__strip');
        stripTrack = root.querySelector('.lightbox__strip-track');
        prevBtn = root.querySelector('.lightbox__nav--prev');
        nextBtn = root.querySelector('.lightbox__nav--next');

        root.querySelector('.lightbox__close').addEventListener('click', close);
        prevBtn.addEventListener('click', function () { show(index - 1); });
        nextBtn.addEventListener('click', function () { show(index + 1); });

        stripTrack.addEventListener('click', function (e) {
            var thumb = e.target.closest('.lightbox__thumb');
            if (!thumb) return;
            var i = parseInt(thumb.dataset.index, 10);
            if (!isNaN(i)) show(i);
        });

        document.body.appendChild(root);
    }

    function isOpen() {
        return !!root && root.classList.contains('lightbox--open');
    }

    function open(itemList, startIndex, title) {
        if (!root) create();
        items = itemList || [];
        index = startIndex || 0;

        titleEl.textContent = title || '';
        titleEl.style.display = title ? '' : 'none';

        root.style.backgroundColor = ''; // left faded by a swipe-down close
        buildSlides();
        buildStrip();
        show(index, true);

        if (!isOpen()) {
            savedOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            // Stops Android pull-to-refresh during a drag-down close.
            savedOverscroll = document.documentElement.style.overscrollBehavior;
            document.documentElement.style.overscrollBehavior = 'none';
        }

        // If a close() is still stepping back, the popstate handler pushes
        // the new entry once that lands.
        if (!onOwnEntry && !backPending) {
            history.pushState({ seemeLightbox: true }, '');
            onOwnEntry = true;
        }

        root.offsetHeight; // force reflow for transition
        root.classList.add('lightbox--open');
    }

    function hide() {
        if (!isOpen()) return;
        root.classList.remove('lightbox--open');
        document.body.style.overflow = savedOverflow;
        document.documentElement.style.overscrollBehavior = savedOverscroll;
    }

    // Drop the entry open() pushed.  history.back() is async; the popstate
    // handler swallows the event it causes.
    function stepBack() {
        onOwnEntry = false;
        backPending = true;
        history.back();
    }

    function close() {
        hide();
        if (onOwnEntry) stepBack();
    }

    // Moving into or out of the lightbox's own history entry only closes the
    // viewer.  Capture phase + stopImmediatePropagation keeps host popstate
    // handlers (sheet routers, HTMX history fallbacks) from reloading the page
    // for an entry they did not create.
    window.addEventListener('popstate', function (e) {
        var arriving = !!(e.state && e.state.seemeLightbox);

        if (backPending) {
            backPending = false;
            e.stopImmediatePropagation();
            // Reopened before our back() landed: give it its own entry now.
            if (isOpen()) {
                history.pushState({ seemeLightbox: true }, '');
                onOwnEntry = true;
            }
            return;
        }

        if (!arriving && !onOwnEntry) return;
        e.stopImmediatePropagation();

        if (arriving) {
            // Forward onto a closed viewer's entry: step back off it so it
            // doesn't swallow the next Back.
            if (!isOpen()) stepBack();
            else onOwnEntry = true;
        } else {
            onOwnEntry = false;
            hide();
        }
    }, true);

    // -- Slides ------------------------------------------------------------

    function buildSlides() {
        trackEl.innerHTML = '';
        for (var i = 0; i < items.length; i++) {
            var slide = document.createElement('div');
            slide.className = 'lightbox__slide';
            var img = document.createElement('img');
            img.className = 'lightbox__image';
            img.alt = items[i].alt || '';
            img.draggable = false;
            slide.appendChild(img);
            trackEl.appendChild(slide);
        }
    }

    // Show the thumbnail (blurred) right away and swap in the full image once
    // it has loaded.  Runs once per slide per open.
    function loadSlide(i) {
        var slide = trackEl.children[i];
        if (!slide || slide.dataset.loaded) return;
        slide.dataset.loaded = '1';

        var img = slide.firstChild;
        var item = items[i];
        if (!item.thumb || item.thumb === item.src) {
            img.src = item.src;
            return;
        }

        img.src = item.thumb;
        img.classList.add('lightbox__image--loading');
        var full = new Image();
        full.onload = full.onerror = function () {
            img.src = item.src;
            img.classList.remove('lightbox__image--loading');
        };
        full.src = item.src;
    }

    // Position the track on the current slide, offset by an in-progress drag.
    function setTrack(dx, dy, instant) {
        trackEl.classList.toggle('lightbox__track--dragging', !!instant);
        trackEl.style.transform =
            'translate3d(calc(' + (-index * 100) + '% + ' + (dx || 0) + 'px), ' + (dy || 0) + 'px, 0)';
    }

    function show(i, instant) {
        if (i < 0 || i >= items.length) return;
        // Jumps past a neighbor skip the slide animation, which would sweep
        // across slides that haven't loaded.
        if (Math.abs(i - index) > 1) instant = true;
        index = i;

        setTrack(0, 0, instant);
        if (instant) trackEl.offsetHeight; // apply the jump before re-enabling the transition
        trackEl.classList.remove('lightbox__track--dragging');

        loadSlide(i);
        loadSlide(i + 1);
        loadSlide(i - 1);

        counterEl.textContent = (i + 1) + ' / ' + items.length;
        originalEl.href = items[i].src;

        prevBtn.disabled = (i === 0);
        nextBtn.disabled = (i === items.length - 1);

        var thumbs = stripTrack.children;
        for (var t = 0; t < thumbs.length; t++) {
            thumbs[t].classList.toggle('lightbox__thumb--active', t === i);
        }

        if (thumbs[i]) {
            thumbs[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }

    function buildStrip() {
        stripTrack.innerHTML = '';
        if (items.length <= 1) {
            stripEl.style.display = 'none';
            return;
        }
        stripEl.style.display = '';

        for (var i = 0; i < items.length; i++) {
            var btn = document.createElement('button');
            btn.className = 'lightbox__thumb';
            btn.type = 'button';
            btn.dataset.index = i;
            var img = document.createElement('img');
            img.src = items[i].thumb;
            img.alt = '';
            img.loading = 'lazy';
            btn.appendChild(img);
            stripTrack.appendChild(btn);
        }
    }

    // -- Keyboard ---------------------------------------------------------
    // Capture phase + stopImmediatePropagation so the lightbox takes priority
    // over the overlay system (otherwise Escape also closes the host sheet/modal).
    document.addEventListener('keydown', function (e) {
        if (!isOpen()) return;
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopImmediatePropagation();
            close();
        } else if (e.key === 'ArrowLeft') {
            show(index - 1);
        } else if (e.key === 'ArrowRight') {
            show(index + 1);
        }
    }, true);

    // -- Auto-bind: photo-grid inside [data-lightbox-group] ---------------
    document.addEventListener('click', function (e) {
        if (e.target.closest('.photo-grid__actions, .photo-grid__remove, .photo-grid__add')) return;

        var item = e.target.closest('[data-lightbox-group] .photo-grid__item');
        if (!item) return;
        e.preventDefault();

        var group = item.closest('[data-lightbox-group]');
        var groupItems = Array.from(
            group.querySelectorAll('.photo-grid__item:not(.photo-grid__add)')
        );
        var lightboxItems = [];
        var clickIndex = 0;
        for (var i = 0; i < groupItems.length; i++) {
            var img = groupItems[i].querySelector('img');
            if (!img) continue;
            if (groupItems[i] === item) clickIndex = lightboxItems.length;
            lightboxItems.push({
                src: groupItems[i].dataset.lightboxSrc || groupItems[i].getAttribute('href') || img.src,
                thumb: img.src,
                alt: img.alt || ''
            });
        }

        if (lightboxItems.length) open(lightboxItems, clickIndex, group.dataset.lightboxTitle);
    });

    // -- Touch: drag to change photos, drag down to close -----------------
    // drag is null when no one-finger gesture is being tracked.  A second
    // finger or a pinch-zoomed page cancels it so native zoom and pan win.
    var drag = null;

    function isZoomed() {
        return !!window.visualViewport && window.visualViewport.scale > 1.01;
    }

    function cancelDrag() {
        if (!drag) return;
        drag = null;
        root.style.backgroundColor = '';
        setTrack(0, 0);
    }

    document.addEventListener('touchstart', function (e) {
        if (!isOpen() || !e.target.closest('.lightbox__stage')) return;
        if (e.touches.length > 1 || isZoomed()) {
            cancelDrag();
            return;
        }
        drag = { x: e.touches[0].clientX, y: e.touches[0].clientY, dx: 0, dy: 0, axis: null };
    }, { passive: true });

    document.addEventListener('touchmove', function (e) {
        if (!drag) return;
        if (e.touches.length > 1) {
            cancelDrag();
            return;
        }
        drag.dx = e.touches[0].clientX - drag.x;
        drag.dy = e.touches[0].clientY - drag.y;

        if (!drag.axis) {
            if (Math.abs(drag.dx) < 8 && Math.abs(drag.dy) < 8) return;
            drag.axis = Math.abs(drag.dx) > Math.abs(drag.dy) ? 'x' : 'y';
        }

        if (drag.axis === 'x') {
            // Resist past the first and last photo.
            var atEdge = (drag.dx > 0 && index === 0) || (drag.dx < 0 && index === items.length - 1);
            setTrack(atEdge ? drag.dx / 3 : drag.dx, 0, true);
        } else if (drag.dy > 0) {
            setTrack(0, drag.dy, true);
            var fade = 1 - Math.min(drag.dy / 400, 0.6);
            root.style.backgroundColor = 'rgba(0, 0, 0, ' + (0.94 * fade) + ')';
        }
    }, { passive: true });

    document.addEventListener('touchend', function (e) {
        if (!drag || e.touches.length) return;
        var d = drag;
        drag = null;

        if (d.axis === 'x' && Math.abs(d.dx) > 50) {
            var next = d.dx > 0 ? index - 1 : index + 1;
            if (next >= 0 && next < items.length) {
                show(next);
                return;
            }
        } else if (d.axis === 'y' && d.dy > 100) {
            close();
            return;
        }
        root.style.backgroundColor = '';
        setTrack(0, 0);
    }, { passive: true });

    document.addEventListener('touchcancel', cancelDrag, { passive: true });

    return { open: open, close: close };
})();

window.SeeMe = SeeMe;
